<template>
  <availabilities-edit-dialog :availabilities="availabilities" :initializing="initializing" :labels="labels" :removing="loadingAvailabilityUnset"
                              :saving="loadingAvailabilityUpdate" :subtitle="subtitle" :value="value" icon="mdi-domain" removable
                              unavailable-text="Wird als nicht verfügbar gespeichert – der Standort erscheint als nicht verfügbar"
                              @input="reset" @remove="saveUnset" @save="saveUpdate"/>
</template>

<script lang="ts">
import Vue from 'vue';
import {LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {Institution} from '@/interfaces/institution.interface';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {InstitutionFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/InstitutionFields';
import mongoose from 'mongoose';
import {EventBus} from '@/busses/EventBus';
import AvailabilitiesEditDialog from '@/components/common/availability/availabilitiesEditDialog.vue';
import {LABELS_WITH_MAKEUP} from '@/components/common/availability/availabilityEditor.helper';

/** Loads and stores the labeled availabilities of an institution; UI lives in the shared availabilities dialog */
export default Vue.extend({
  components: {AvailabilitiesEditDialog},
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    institutionId: {
      type: mongoose.Types.ObjectId,
      required: true,
    },
  },
  data: () => ({
    /** Standort / Lehrkraft may mark windows "Für Nachholtermine" */
    labels: LABELS_WITH_MAKEUP,
    initializing: false,
    loadingAvailabilityUpdate: false,
    loadingAvailabilityUnset: false,
    /** null = no information stored */
    availabilities: null as LabeledAvailabilityInterface[] | null,
    institution: undefined as unknown as Institution,
  }),
  computed: {
    subtitle(): string {
      return this.institution?.name || 'Standort';
    },
  },
  methods: {
    async initialize() {
      this.initializing = true;
      try {
        this.institution = await InstitutionerApi
            .findById(this.institutionId, {fields: [InstitutionFields.AVAILABILITIES, InstitutionFields.NAME]});
        this.availabilities = this.institution.availabilities || null;
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Verfügbarkeiten', 'error');
        this.$emit('input', false);
      } finally {
        this.initializing = false;
      }
    },
    async saveUpdate(availabilities: LabeledAvailabilityInterface[]) {
      this.loadingAvailabilityUpdate = true;
      try {
        await InstitutionerApi.update(this.institutionId, {
          availabilities,
        });
        this.$$showSnackbar('Die Verfügbarkeiten wurden erfolgreich gespeichert', 'success');
        this.$emit('update:availabilities', availabilities);
        EventBus.$emit('update:availabilities');
        this.reset();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Speichern der Verfügbarkeiten', 'error');
      } finally {
        this.loadingAvailabilityUpdate = false;
      }
    },
    /** Former "Verfügbarkeiten verwerfen": unsets the availabilities (no information instead of "not available") */
    async saveUnset() {
      this.loadingAvailabilityUnset = true;
      try {
        await InstitutionerApi.update(this.institutionId, {availabilities: null});
        this.$$showSnackbar('Verfügbarkeiten wurden verworfen', 'success');
        this.$emit('update:availabilities', undefined);
        EventBus.$emit('update:institution');
        this.reset();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Löschen der Verfügbarkeiten', 'error');
      } finally {
        this.loadingAvailabilityUnset = false;
      }
    },
    reset() {
      this.$emit('input', false);
      setTimeout(() => {
        this.institution = undefined as unknown as Institution;
        this.availabilities = null;
      }, 300);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v) {
          this.initialize();
        }
      },
    },
  },
});
</script>
